import React from 'react';
import styled from 'styled-components';
import {
  Badge,
  Btn,
  Empty,
  Loading,
  SEGMENT_LABEL,
  SEGMENT_TONE,
  T,
  Table,
  TableScroll
} from './ui';

/**
 * The audience table from the dashboard design.
 *
 * Nine columns, sortable on five of them, paged with the cursor the endpoint returns. The columns
 * are exactly what the endpoint projects — adding one here means adding it to the projection too,
 * which is deliberate friction: the query is narrow so it never fetches whole user documents.
 */
const COLUMNS = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'country', label: 'Country', sort: 'country' },
  // Not sortable: the team is read from the fan's subscriptions and follows for each page, so there
  // is no stored field for the server to order the whole audience by.
  { key: 'team', label: 'Team' },
  { key: 'segment', label: 'Segment', sort: 'segment' },
  { key: 'subscription', label: 'Subscription' },
  { key: 'lastActive', label: 'Last Active', sort: 'lastActive' },
  { key: 'verified', label: 'Verified' }
];

/*
  The segment badge, as the design renders it: a filled pill, not coloured text.

  The pill is doing work rather than decoration. Segment is the one derived column in the table —
  every other value is stored on the user, while this is computed from `lastActiveAt` against the
  configured thresholds. Giving it a shape of its own is what stops an operator reading it as a
  field they could go and edit.

  Its tone and its word now come from `ui.js`, beside the campaign-status maps. The label is
  capitalised in the text rather than with `text-transform`, which is why it is a map at all: a CSS
  transform changes the pixels and not the string, so the cell would still read `engaged` to a
  copy-paste, a screen reader or an export.
*/

const relative = (value) => {
  if (!value) return '—';

  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.round(diff / 60000);
  if (minutes < 60) return `${Math.max(minutes, 1)} mins ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.round(hours / 24);
  if (days < 31) return `${days} day${days === 1 ? '' : 's'} ago`;

  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;

  return new Date(value).toLocaleDateString();
};

/*
  Looks like the header text it replaced, behaves like the button it always should have been.

  Inherits font and colour so the table's appearance does not change; the only visible difference
  is a focus ring, which is the point.
*/
const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  user-select: none;

  &:focus-visible {
    outline: 2px solid ${T.primary};
    outline-offset: 2px;
  }
`;

const SortMark = ({ active, direction }) => {
  if (!active) return null;
  return <span style={{ color: T.primary }}>{direction === 'asc' ? ' ↑' : ' ↓'}</span>;
};

const AudienceTable = ({ rows = [], sort, onSortChange, onLoadMore, hasMore, loading }) => {
  const toggleSort = (field) => {
    if (!field) return;
    const direction = sort?.field === field && sort?.direction === 'desc' ? 'asc' : 'desc';
    onSortChange({ field, direction });
  };

  /*
    Loading is checked before emptiness, and says so.

    `!loading && rows.length === 0` fell through during the first fetch, so the table rendered bare
    column headers with no body and no indication that anything was happening — which reads as
    "nobody matches" rather than "still asking".
  */
  if (loading && rows.length === 0) {
    return <Loading title="Counting the audience…" />;
  }

  if (rows.length === 0) {
    return (
      <Empty title="No users match these filters">
        Widen or clear a filter to see recipients. An empty result and an unapplied filter look the
        same in the count, which is why this says which it is.
      </Empty>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <TableScroll>
        {/* Named because the landing page carries the campaign list too, and "table" twice
            over tells a screen-reader user nothing about which one they have landed in. */}
        <Table aria-label="Matching users">
          <thead>
            <tr>
              {COLUMNS.map(({ key, label, sort: sortField }) => (
                <th
                  key={key}
                  aria-sort={
                    sortField && sort?.field === sortField
                      ? sort.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : undefined
                  }
                >
                  {/*
                    A real button inside the cell, for the sortable columns.

                    Sorting was an `onClick` on the bare `<th>`: reachable only with a mouse, and
                    marked with a correct `aria-sort` that nothing could get to. The button carries
                    the click; the `<th>` keeps the sort state, which is where a screen reader
                    expects to find it.

                    Unsortable columns render plain text rather than a disabled button — four of
                    the nine are not sortable, and a button that cannot do anything is noise in the
                    tab order.
                  */}
                  {sortField ? (
                    <SortButton type="button" onClick={() => toggleSort(sortField)}>
                      {label}
                      <SortMark
                        active={sortField && sort?.field === sortField}
                        direction={sort?.direction}
                      />
                    </SortButton>
                  ) : (
                    label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row._id}>
                <td style={{ color: T.heading, fontWeight: 500 }}>{row.firstName || '—'}</td>
                <td style={{ color: T.heading, fontWeight: 500 }}>{row.lastName || '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{row.email}</td>
                <td>{row.country || '—'}</td>
                <td>{row.team || '—'}</td>
                <td>
                  {row.segment ? (
                    <Badge tone={SEGMENT_TONE[row.segment] || 'slate'}>
                      {SEGMENT_LABEL[row.segment] || row.segment}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {row.subscription ? (
                    <Badge tone={row.subscription === 'Subscribed' ? 'green' : 'slate'}>
                      {row.subscription}
                    </Badge>
                  ) : (
                    '—'
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{relative(row.lastActive)}</td>
                <td>
                  <Badge tone={row.verified ? 'green' : 'red'}>{row.verified ? 'Yes' : 'No'}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableScroll>

      {hasMore ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Btn variant="outline" onClick={onLoadMore} disabled={loading}>
            {loading ? 'Loading…' : 'Load more users'}
          </Btn>
        </div>
      ) : null}
    </div>
  );
};

export default AudienceTable;
